import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import type { TodayClassView } from '@/types/coaching'
import { formatScheduleDays } from './todayClassUtils'

interface Props {
  classes: TodayClassView[]
  selectedId: number | null
  onSelect: (timeslotId: number) => void
}

export function TodayClassList({ classes, selectedId, onSelect }: Props) {
  const { t } = useTranslation('coaching')
  const dayLabel = (day: string) => t(`classList.day.${day}`, day)

  if (classes.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 p-6 text-sm text-gray-500" role="status">
        {t('classList.empty')}
      </div>
    )
  }

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={t('classList.groupLabel')}>
      {classes.map((c) => {
        const selected = c.timeslotId === selectedId
        return (
          <button
            key={c.timeslotId}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(c.timeslotId)}
            className={clsx(
              'rounded-lg border px-3 py-2 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500',
              selected
                ? 'border-emerald-400 bg-emerald-100/60 ring-2 ring-emerald-200'
                : 'border-gray-200 bg-white hover:bg-emerald-50/60',
            )}
          >
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{c.name}</span>
              {c.inProgress && (
                <span className="rounded-full bg-lc-mint-100 px-2 py-0.5 text-xs font-semibold text-lc-mint-900">
                  {t('classList.inProgress')}
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
              <span>{formatScheduleDays(c.scheduleDays, dayLabel) || '-'}</span>
              <span>{c.startTime}</span>
              <span>{t('classList.minutes', { n: c.durationMinutes })}</span>
              <span>{t('classList.countUnit', { n: c.studentCount })}</span>
              <span>{c.teacherName ?? t('classList.noTeacher')}</span>
            </div>
          </button>
        )
      })}
    </div>
  )
}
